import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt, FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../FeeType/styles.module.css";
import { CustomDropdown, CustomDatePicker } from "../../../../components";
import {
  fetchClassOptions,
  fetchSectionOptions,
  clearSectionOptions,
} from "../../../../redux/Masterdata";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useDispatch, useSelector } from "react-redux";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

/* ── Inline styles ── */
const S = {
  statusBadge: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 700,
    color: "#fff",
    whiteSpace: "nowrap",
  },
  viewBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    background: "#f97316",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  panelWrap: { padding: "4px 0 8px 24px", background: "#fff" },
  panelTable: { width: "100%", borderCollapse: "collapse" },
  panelTh: {
    fontSize: 13,
    fontWeight: 700,
    color: "#1e3a5f",
    padding: "12px 16px",
    textAlign: "left",
    background: "#f3f4f6",
  },
  panelCell: {
    padding: "14px 16px",
    fontSize: 14,
    color: "#1e3a5f",
    borderBottom: "1px solid #e5e7eb",
  },
  panelEmpty: {
    textAlign: "center",
    padding: 24,
    color: "#6b7280",
    fontSize: 14,
  },
  headerDateWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 180,
  },
};

/* ── Fee status badge: Paid = green, Partial = orange, Not Paid = red ── */
const FeeStatusBadge = ({ status }) => {
  const s = String(status || "").toLowerCase();
  let bg = "#dc2626"; // Not Paid
  if (s === "paid") bg = "#16a34a";
  else if (s === "partially paid") bg = "#f97316";
  return (
    <span style={{ ...S.statusBadge, backgroundColor: bg }}>
      {status || "Not Paid"}
    </span>
  );
};

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);

const formatDate = (val) => {
  if (!val) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return val;
};

/* ────────────────────────────────────────────
   Expandable row panel — payment history.
──────────────────────────────────────────── */
const TransactionPanel = ({ row }) => {
  const { translations } = useSelector((state) => state.language);
  const history = row?.original?.payment_history || [];

  if (history.length === 0) {
    return (
      <div style={S.panelEmpty}>
        {translations?.labels?.noHistoryFound || "No history found"}
      </div>
    );
  }

  return (
    <div style={S.panelWrap}>
      <table style={S.panelTable}>
        <thead>
          <tr>
            <th style={S.panelTh}>{translations?.labels?.date || "Date"}</th>
            <th style={S.panelTh}>
              {translations?.labels?.paymentMode || "Payment Mode"}
            </th>
            <th style={S.panelTh}>
              {translations?.labels?.upiTransactionId || "UPI Transaction ID"}
            </th>
            <th style={S.panelTh}>{translations?.labels?.paid || "Paid"}</th>
            <th style={S.panelTh}>
              {translations?.labels?.balance || "Balance"}
            </th>
          </tr>
        </thead>
        <tbody>
          {history.map((h, idx) => (
            <tr key={idx}>
              <td style={S.panelCell}>{h.date || "-"}</td>
              <td style={S.panelCell}>{h.payment_mode || "-"}</td>
              <td style={S.panelCell}>{h.upi_transaction_id || "-"}</td>
              <td style={S.panelCell}>{formatCurrency(h.paid)}</td>
              <td style={S.panelCell}>{formatCurrency(h.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ListCollectFees = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const master_id = clientData?.master_id || "";
  const academic_year_id =
    clientData?.academic_year_id || clientData?.academic_id || "";
  const academic_year = clientData?.academic_year || "";
  const roleId = Number(user?.role?.id);

  const {
    classOptions: reduxClassOptions,
    staffClassAndSubjectOptions,
    sectionOptions: reduxSectionOptions,
  } = useSelector((state) => state.masterData);

  const [data, setData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ── Filters that feed the payload ──
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [feeStatus, setFeeStatus] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });

  /* ── Deep-link filters (from the recovery-bar section drill-down) ──
     location.state may carry { class_id, class_name, section_id,
     section_name }. Captured once in a ref. */
  const incomingRef = useRef(location.state || null);
  const classAppliedRef = useRef(false);
  const pendingSectionRef = useRef(null);

  /* Whether we still owe a deep-link application. When true, the list
     fetch is HELD so it doesn't fire once with empty filters and then
     again with the applied class/section — a single, correct call.
     No deep link → ready immediately (normal mount behaviour). */
  const hasDeepLink = !!(
    incomingRef.current &&
    (incomingRef.current.class_id || incomingRef.current.class_name)
  );
  const [deepLinkReady, setDeepLinkReady] = useState(!hasDeepLink);

  // ── Fetch class options on mount ──
  useEffect(() => {
    if (school_id) {
      dispatch(
        fetchClassOptions({
          id: parseInt(school_id),
          academic_year: academic_year,
        }),
      );
    }
  }, [dispatch, school_id]);

  // ── Dropdown options ──
  const classOptions = useMemo(() => {
    const sourceData =
      roleId === 3 ? staffClassAndSubjectOptions : reduxClassOptions;
    return [
      {
        value: "",
        label: translations.labels?.selectClass || "Select Class",
      },
      ...sourceData.map((item) => ({
        value: item.class_id ?? item.value,
        label: item.class_name ?? item.label,
      })),
    ];
  }, [reduxClassOptions, staffClassAndSubjectOptions, roleId, translations]);

  const sectionOptions = useMemo(
    () => [
      {
        value: "",
        label: translations.labels?.selectSection || "Select Section",
      },
      ...reduxSectionOptions,
    ],
    [reduxSectionOptions, translations],
  );

  const feeStatusOptions = useMemo(
    () => [
      { value: "", label: translations.labels?.all || "All" },
      { value: "Paid", label: translations.labels?.paid || "Paid" },
      {
        value: "Not Paid",
        label: translations.labels?.notPaid || "Not Paid",
      },
    ],
    [translations],
  );

  /* ── Deep-link step 1: resolve + apply the class ──
     Runs when classOptions change (so a name can be matched once the
     list loads). If an id was passed we don't need to wait. */
  useEffect(() => {
    if (classAppliedRef.current) return;
    const incoming = incomingRef.current;
    if (!incoming || (!incoming.class_id && !incoming.class_name)) {
      classAppliedRef.current = true;
      setDeepLinkReady(true);
      return;
    }

    let cid = incoming.class_id;

    if (cid) {
      /* An id was passed (e.g. 66). Wait until classOptions actually
         contains it — otherwise the dropdown has no label to show and
         renders the raw id ("66") instead of the class name ("VI").
         Values may be number or string, so compare as strings. */
      const opt = classOptions.find(
        (o) => o.value !== "" && String(o.value) === String(cid),
      );
      if (!opt) {
        // options loaded but id absent → give up; still loading → wait
        if (classOptions.length > 1) {
          classAppliedRef.current = true;
          setDeepLinkReady(true);
        }
        return;
      }
      // use the option's own value so its type matches the option list
      cid = opt.value;
    } else if (incoming.class_name) {
      const opt = classOptions.find(
        (o) => o.value !== "" && String(o.label) === String(incoming.class_name),
      );
      if (!opt) {
        // No match. If the class list has actually loaded (has real
        // entries), the name isn't there → stop waiting and just load
        // unfiltered. Otherwise keep waiting for the next render.
        if (classOptions.length > 1) {
          classAppliedRef.current = true;
          setDeepLinkReady(true);
        }
        return;
      }
      cid = opt.value;
    }
    if (!cid) {
      classAppliedRef.current = true;
      setDeepLinkReady(true);
      return;
    }

    classAppliedRef.current = true;
    setSelectedClass(cid);
    dispatch(clearSectionOptions());
    dispatch(
      fetchSectionOptions({
        id: [parseInt(cid)],
        school_id: parseInt(school_id),
        staff_id: parseInt(master_id),
        subject_staff: 1,
      }),
    );

    if (incoming.section_id || incoming.section_name) {
      // still owe a section → keep the fetch held until step 2 resolves
      pendingSectionRef.current = {
        section_id: incoming.section_id ?? null,
        section_name: incoming.section_name ?? null,
      };
    } else {
      // class only → release the fetch now
      setDeepLinkReady(true);
    }
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [classOptions, dispatch, school_id, master_id]);

  /* ── Deep-link step 2: resolve + apply the section ──
     Fires when reduxSectionOptions populate after the class's fetch.
     Releases the held fetch once done (matched or not). */
  useEffect(() => {
    const pend = pendingSectionRef.current;
    if (!pend) return;

    let sid = pend.section_id;
    if (!sid && pend.section_name) {
      const opt = reduxSectionOptions.find(
        (o) => String(o.label) === String(pend.section_name),
      );
      if (!opt) {
        // options still loading → wait; loaded but absent → give up
        if (reduxSectionOptions.length > 0) {
          pendingSectionRef.current = null;
          setDeepLinkReady(true);
        }
        return;
      }
      sid = opt.value;
    }

    pendingSectionRef.current = null;
    if (sid) {
      setSelectedSection(sid);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
    setDeepLinkReady(true);
  }, [reduxSectionOptions]);

  const fetchStudentList = useCallback(async () => {
    if (!school_id) {
      setData([]);
      setRowCount(0);
      return;
    }

    setTableLoading(true);
    try {
      const response = await AxiosInstance.post("schoolFees/collectFee/list", {
        school_id: Number(school_id),
        academic_year_id: Number(academic_year_id),
        class_id: selectedClass ? Number(selectedClass) : "",
        section_id: selectedSection || "",
        start_date: startDate || "",
        end_date: endDate || "",
        fee_status: feeStatus || "",
        search: apiSearch.trim(),
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      if (response?.data?.status && Array.isArray(response.data.data)) {
        const transformed = response.data.data.map((item, index) => ({
          fee_id: item.id || "",
          admission_id: item.admission_id || "",
          group_name: item.group_name || "",
          s_no: pagination.pageIndex * pagination.pageSize + index + 1,
          student: item.student_name || "",
          total_amount: Number(item.total_amount) || 0,
          net_amount: Number(item.net_amount) || 0,
          paid_amount: Number(item.paid_amount) || 0,
          balance_amount: Number(item.balance_amount) || 0,
          due_date: item.due_date || "",
          fee_status: item.fee_status || "Not Paid",
          payment_history: Array.isArray(item.payment_history)
            ? item.payment_history
            : [],
          student_id: item.student_id || "",
        }));
        setData(transformed);
        setRowCount(response.data.pagination?.total || transformed.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching collect fee list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setTableLoading(false);
    }
  }, [
    school_id,
    academic_year_id,
    selectedClass,
    selectedSection,
    startDate,
    endDate,
    feeStatus,
    apiSearch,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  /* Held until the deep-link (if any) has been applied, so the first
     request already carries the class/section — no empty pre-call. */
  useEffect(() => {
    if (!deepLinkReady) return;
    fetchStudentList();
  }, [fetchStudentList, deepLinkReady]);

  // ── Handlers ──
  const handleClassChange = useCallback(
    (value) => {
      const classId = typeof value === "object" ? value?.value : value;
      setSelectedClass(classId);
      setSelectedSection("");
      dispatch(clearSectionOptions());
      if (classId) {
        dispatch(
          fetchSectionOptions({
            id: [parseInt(classId)],
            school_id: parseInt(school_id),
            staff_id: parseInt(master_id),
            subject_staff: 1,
          }),
        );
      }
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [dispatch, school_id, master_id],
  );

  const handleSectionChange = useCallback((value) => {
    const sectionId = typeof value === "object" ? value?.value : value;
    setSelectedSection(sectionId);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // ── Sync MRT's native filter select into `feeStatus` ──
  useEffect(() => {
    const statusFilter = columnFilters.find((f) => f.id === "fee_status");
    const status = statusFilter?.value ?? "";
    setFeeStatus((prev) => (prev === status ? prev : status));
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [columnFilters]);

  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleView = useCallback(
    (row) => {
      navigate("/user/payfees", {
        state: {
          id: row?.fee_id,
          admission_id: row.admission_id,
          student_id: row.student_id,
          class_id: selectedClass,
          section_id: selectedSection,
          data: row,
        },
      });
    },
    [navigate, selectedClass, selectedSection],
  );

  const handleModalOk = () =>
    setModalState((prev) => ({ ...prev, open: false }));

  // ── Columns ──
  const columns = useMemo(
    () => [
      {
        accessorKey: "s_no",
        header: translations.labels?.serialNo || "S.No",
        size: 80,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student",
        header: translations.labels?.student || "Student",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "group_name",
        header: translations.labels?.groupNmae || "Group Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "paid_amount",
        header: translations.labels?.paidAmount || "Paid Amount (₹)",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "balance_amount",
        header: translations.labels?.balanceAmount || "Balance Amount (₹)",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "net_amount",
        header: translations.labels?.totalAmount || "Total Amount (₹)",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "due_date",
        header: translations.labels?.dueDate || "Due Date",
        enableSorting: true,
        enableColumnFilter: true,
        /* Server-side. The payload takes a RANGE, so the header
           filter is a from/to pair writing to startDate/endDate. */
        Filter: () => {
          const startValue = startDate ? dayjs(startDate) : null;
          const endValue = endDate ? dayjs(endDate) : null;
          return (
            <div style={S.headerDateWrap} onClick={(e) => e.stopPropagation()}>
              <CustomDatePicker
                name="due_date_start"
                value={startValue}
                onChange={(d) => {
                  setStartDate(d ? dayjs(d).format("YYYY-MM-DD") : "");
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
                placeholder={
                  translations.labels?.startDatePlaceholder || "Start Date"
                }
                format="DD/MM/YYYY"
                size="sm"
                isClearable={true}
                maxDate={endValue || undefined}
              />
              <CustomDatePicker
                name="due_date_end"
                value={endValue}
                onChange={(d) => {
                  setEndDate(d ? dayjs(d).format("YYYY-MM-DD") : "");
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
                placeholder={
                  translations.labels?.endDatePlaceholder || "End Date"
                }
                format="DD/MM/YYYY"
                size="sm"
                isClearable={true}
                minDate={startValue || undefined}
              />
            </div>
          );
        },
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "fee_status",
        header: translations.labels?.feeStatus || "Fees Status",
        enableSorting: true,
        enableColumnFilter: true,
        filterVariant: "select",
        filterSelectOptions: feeStatusOptions,
        size: 160,
        minSize: 160,
        maxSize: 160,
        Cell: ({ cell }) => <FeeStatusBadge status={cell.getValue()} />,
      },
      {
        id: "actions",
        header: translations.labels?.actions || "actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            type="button"
            style={S.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations, handleView, startDate, endDate, feeStatusOptions],
  );

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleModalOk}
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.collectFess || "Collect Fees"}
          </h1>
        </div>

        {/* ── Filters (all feed the payload) ── */}
        <div className="row" style={{ marginBottom: 16 }}>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.class || "Class"}
              name="filterClass"
              value={selectedClass}
              onChange={handleClassChange}
              options={classOptions}
              placeholder={translations.labels?.selectClass || "Select Class"}
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.section || "Section"}
              name="filterSection"
              value={selectedSection}
              onChange={handleSectionChange}
              options={sectionOptions}
              placeholder={
                translations.labels?.selectSection || "Select Section"
              }
              disabled={!selectedClass}
            />
          </div>
        </div>

        {/* ── Table ── */}
        <div className={styles.tableSection}>
          <CustomMRTTable
            columns={columns}
            data={data}
            loading={tableLoading}
            pagination={pagination}
            rowCount={rowCount}
            placeholder={
              translations?.labels?.searchStudentName || "Search Student Name"
            }
            setSearchInput={setSearchInput}
            onSearchEnter={handleSearchEnter}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            enableSearch={true}
            enableFiltering={true}
            manualPagination={true}
            enableExpanding={true}
            renderDetailPanel={({ row }) => <TransactionPanel row={row} />}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
          />
        </div>
      </div>
    </>
  );
};

export default ListCollectFees;
  